import { Disclosure, Importance, SmartJd } from '../../models/smart-jd.model';
import {
  MatchRequirementItem,
  MatchRequirementTally,
  MatchScoreDetail,
  MatchScreeningItem,
  MatchVerdict,
  MatchedCandidate,
} from '../../models/match-result.model';

/** [verdict, evidence quote, reasoning] keyed by requirement id. */
export type MockVerdict = [string, string, string];
export type MockVerdictBank = Record<string, MockVerdict>;

/** NR has no stated meaning in the contract; the mock scores it like an unclassified one. */
const WEIGHT: Record<Importance, number> = { NN: 3, EXP: 2, DIFF: 1, NR: 1, UNKNOWN: 1 };

/**
 * MOCK ONLY. Simulates the backend scorer so the UI can be built without Recruit Service.
 * It reproduces the sample result: NN weight 3, EXP weight 2, DIFF weight 1, partial = half points,
 * optional requirements that are not in the resume are not counted.
 */
export interface MockIdentity {
  uuid: string;
  name: string;
  title: string;
  company: string;
  location: string;
}

/** The Published Smart JD a candidate was scored against, as the match result reports it. */
export interface MockScoredAgainst {
  uuid: string;
  version: number;
}

export function scoreCandidate(
  who: MockIdentity,
  smart: SmartJd,
  bank: MockVerdictBank,
  against: MockScoredAgainst,
  matchVersion: number,
  matchedAt: string,
): MatchedCandidate {
  const requirements = smart.requirements ?? [];
  const resumeReqs = requirements.filter((r) => r.assessed_in === 'resume');
  const verdicts: MatchVerdict[] = resumeReqs.map((r) => {
    const [verdict, evidence, reasoning] = bank[r.id] ?? ['not_available', '', 'No evidence found in the resume for this requirement.'];
    return { requirement_id: r.id, criteria: r.criteria, verdict, evidence_type: evidence ? 'direct' : 'none', evidence, reasoning };
  });

  const details: MatchScoreDetail[] = verdicts.map((v, i) => {
    const r = resumeReqs[i];
    const weight = WEIGHT[r.importance];
    const counted = r.importance === 'NN' || v.verdict !== 'not_available';
    const points = v.verdict === 'match' ? weight : v.verdict === 'partial' ? weight / 2 : 0;
    return { id: r.id, criteria: r.criteria, importance: r.importance, verdict: v.verdict, counted, weight, points, evidence: v.evidence, reasoning: v.reasoning };
  });

  const band = (importance: Importance): MatchRequirementItem[] =>
    resumeReqs
      .map((r, i) => ({ r, verdict: verdicts[i].verdict }))
      .filter(({ r }) => r.importance === importance)
      .map(({ r, verdict }) => ({ id: r.id, criteria: r.criteria, status: statusOf(verdict, r.disclosure) }));

  const screening: MatchScreeningItem[] = [
    ...requirements
      .filter((r) => r.assessed_in === 'screening')
      .map((r) => ({ id: r.id, criteria: r.criteria, importance: r.importance, reason: 'screening_only' as const })),
    ...details
      .filter((d) => d.importance !== 'NN' && d.verdict === 'not_available')
      .map((d) => ({ id: d.id, criteria: d.criteria, importance: d.importance, reason: 'not_stated_optional' as const })),
    ...details
      .filter((d) => d.verdict === 'partial')
      .map((d) => ({ id: d.id, criteria: d.criteria, importance: d.importance, reason: 'partial_evidence' as const })),
  ];

  const failed = details.filter((d) => d.importance === 'NN' && d.verdict === 'not_available');
  const nonNegotiables = tally(band('NN'));
  const verdict = verdictOf(nonNegotiables, scoreOf(details));

  return {
    candidate_uuid: who.uuid,
    matched_at: matchedAt,
    match_version: matchVersion,
    smart_jd_uuid: against.uuid,
    smart_jd_version: against.version,
    name: who.name,
    title: who.title,
    company: who.company,
    location: who.location,
    status: 'completed',
    verdicts,
    score: {
      verdict,
      // Live responses send no number for these, so mock mode has to show the dash too.
      score: NUMBERLESS.includes(verdict) ? null : scoreOf(details),
      coverage: requirements.length ? Math.floor((verdicts.length / requirements.length) * 100) : 0,
      non_negotiables: nonNegotiables,
      expected: tally(band('EXP')),
      screening_list: screening,
      rejection_reasons: verdict === 'REJECTED' ? failed.map((d) => ({ id: d.id, criteria: d.criteria, reason: d.reasoning, evidence: d.evidence })) : [],
      details,
    },
  };
}

/**
 * The two verdicts observed arriving with no number: a live `INSUFFICIENT_DATA` carried
 * `score: null` beside a coverage figure, and recruit's own domain notes that a rejected candidate
 * can carry coverage and no score. `NEEDS_REVIEW` is a gate too, but nothing says it loses its
 * number, so the mock does not invent that.
 */
const NUMBERLESS = ['REJECTED', 'INSUFFICIENT_DATA'];

/**
 * The scoring service's own order: a non-negotiable contradicted outranks one that cannot be
 * judged, which outranks one only partly met. Only past all three does the number decide.
 */
function verdictOf(nn: MatchRequirementTally, score: number): string {
  if ((nn.failed ?? 0) > 0) return 'REJECTED';
  if ((nn.unknown ?? 0) > 0) return 'INSUFFICIENT_DATA';
  if ((nn.partial ?? 0) > 0) return 'NEEDS_REVIEW';
  return score >= 80 ? 'STRONG_MATCH' : score >= 60 ? 'POSSIBLE_MATCH' : 'WEAK_MATCH';
}

/** Points over weight across the counted requirements. The mock's own number, not the UI's. */
function scoreOf(details: MatchScoreDetail[]): number {
  const counted = details.filter((d) => d.counted);
  const weight = counted.reduce((a, d) => a + (d.weight ?? 0), 0);
  const points = counted.reduce((a, d) => a + (d.points ?? 0), 0);
  return weight ? Math.round((points / weight) * 100) : 0;
}

/** The real scorer's rule: silence is a failure where a resume would have stated it, an open question where it would not. */
function statusOf(verdict: string, disclosure: Disclosure): string {
  if (verdict === 'match') return 'met';
  if (verdict === 'partial') return 'partial';
  if (verdict === 'no_match') return 'failed';
  return disclosure === 'expected' ? 'failed' : 'unknown';
}

function tally(items: MatchRequirementItem[]): MatchRequirementTally {
  const count = (v: string) => items.filter((i) => i.status === v).length;
  return { total: items.length, met: count('met'), partial: count('partial'), failed: count('failed'), unknown: count('unknown'), items };
}
